import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import { OpeningHoursWithOpeningTimes } from "../store/opening-hours"
import {
  REMINDER_END_HOUR,
  getReminderStartAt,
  isReminderSendDay,
} from "./pickup-reminder-schedule"

dayjs.extend(utc)
dayjs.extend(timezone)

const STOCKHOLM_TZ = "Europe/Stockholm"

/**
 * Minutes before each automatic reminder about a service proposal or addon
 * order, counted from the previous reminder (the first from the send itself).
 */
export const MINUTES_BEFORE_PROPOSAL_REMINDER: readonly number[] = [
  20, 60, 120, 240,
]

/** Automatic reminders before the mechanic is told to call the customer. */
export const MAX_AUTOMATIC_PROPOSAL_REMINDERS =
  MINUTES_BEFORE_PROPOSAL_REMINDER.length

/** After the last reminder, how long the customer gets before the mechanic is told. */
export const MINUTES_BEFORE_MECHANIC_IS_TOLD_ABOUT_PROPOSAL = 60

/** Nothing goes out from 18:00, Stockholm time. */
export const PROPOSAL_REMINDER_WINDOW_END_HOUR = REMINDER_END_HOUR

/** How far ahead to look for an open day before checking again later. */
const MAX_DAYS_TO_SEARCH = 31

/**
 * Whether a proposal reminder may go out at `time`: from when the store opens
 * (09:00 at the earliest) until 18:00, on a day the store is open.
 */
export const isWithinProposalReminderWindow = (
  openingHours: OpeningHoursWithOpeningTimes | null,
  time: Date,
): boolean =>
  isReminderSendDay(openingHours, time) &&
  time >= getReminderStartAt(openingHours, time) &&
  dayjs(time).tz(STOCKHOLM_TZ).hour() < PROPOSAL_REMINDER_WINDOW_END_HOUR

/**
 * `time` itself if a reminder may go out then; otherwise when the store opens
 * on the next open day (today, if it hasn't opened yet). A store with no open
 * day in sight is checked again a month later, so the series picks up when it
 * reopens.
 */
export const moveIntoProposalReminderWindow = (
  openingHours: OpeningHoursWithOpeningTimes | null,
  time: Date,
): Date => {
  if (isWithinProposalReminderWindow(openingHours, time)) return time

  const firstDayOffset = time < getReminderStartAt(openingHours, time) ? 0 : 1
  // Noon keeps the calendar date right across a daylight-saving switch.
  const noon = dayjs(time).tz(STOCKHOLM_TZ).startOf("day").add(12, "hour")

  for (let offset = firstDayOffset; offset < MAX_DAYS_TO_SEARCH; offset++) {
    const day = noon.add(offset, "day").toDate()
    if (isReminderSendDay(openingHours, day)) {
      return getReminderStartAt(openingHours, day)
    }
  }
  return getReminderStartAt(
    openingHours,
    noon.add(MAX_DAYS_TO_SEARCH, "day").toDate(),
  )
}

/**
 * When the next step would be due after `previousAt` (the send, or the
 * previous reminder), before the window is applied: the next reminder while
 * `remindersSent` is below the maximum, otherwise the moment the mechanic is
 * told to call. Enough to schedule the first reminder at send time; the run
 * moves a due time that lands outside the window when it gets there.
 */
export const addProposalReminderDelay = (
  previousAt: Date,
  remindersSent: number,
): Date => {
  const minutes =
    MINUTES_BEFORE_PROPOSAL_REMINDER[remindersSent] ??
    MINUTES_BEFORE_MECHANIC_IS_TOLD_ABOUT_PROPOSAL
  return dayjs(previousAt).add(minutes, "minute").toDate()
}

/** The next step after `previousAt`, moved into the window on an open day. */
export const getNextProposalReminderAt = ({
  previousAt,
  remindersSent,
  openingHours,
}: {
  previousAt: Date
  remindersSent: number
  openingHours: OpeningHoursWithOpeningTimes | null
}): Date =>
  moveIntoProposalReminderWindow(
    openingHours,
    addProposalReminderDelay(previousAt, remindersSent),
  )
